<script setup>
import { ref, computed, watch } from 'vue';
import ModalWrapper from './ModalWrapper.vue';
import { rpc } from '../../../composables/useRpc.js';

const props = defineProps({
  show: { type: Boolean, default: false },
  phone: { type: String, required: true },
  conversationId: { type: String, required: true },
  clientId: { type: String, default: null },
});

const emit = defineEmits(['close', 'created']);

// --- State ---
const step = ref('select'); // 'select' | 'variables' | 'confirm'
const search = ref('');
const items = ref([]);
const selected = ref([]);
const sending = ref(false);
const loadingVars = ref(false);
const qqVariables = ref([]);
const overrides = ref({});

// --- Helpers ---
function hasVariables(qq) {
  return !!qq.needs_variables;
}

function resolveName(name, vars) {
  let resolved = name.replace(/\$(\w+)/g, (_, key) => vars[key] || `$${key}`);
  return resolved.replace(/\s*\(Variable\)\s*/gi, '').trim();
}

// --- Computed ---
const filtered = computed(() => {
  if (!search.value) return items.value;
  const words = search.value.toLowerCase().split(/\s+/);
  return items.value.filter(q => {
    const name = (q.display_name || q.name || '').toLowerCase();
    return words.every(w => name.includes(w));
  });
});

const selectedWithVars = computed(() =>
  items.value.filter(q => selected.value.includes(q.id) && hasVariables(q))
);

const selectedItems = computed(() =>
  items.value.filter(q => selected.value.includes(q.id))
);

const stepIndex = computed(() => {
  if (step.value === 'select') return 0;
  if (step.value === 'variables') return 1;
  return 2;
});

// --- Load catalog on show ---
watch(() => props.show, async (val) => {
  if (val) {
    step.value = 'select';
    search.value = '';
    selected.value = [];
    overrides.value = {};
    qqVariables.value = [];
    await loadQQs();
  }
}, { immediate: true });

async function loadQQs() {
  try {
    const data = await rpc('qq-list');
    if (Array.isArray(data)) items.value = data;
  } catch (err) {
    console.error('[QQ] Failed to load catalog:', err);
  }
}


// --- Actions ---
function toggleItem(id) {
  const idx = selected.value.indexOf(id);
  if (idx >= 0) {
    selected.value.splice(idx, 1);
  } else {
    selected.value.push(id);
  }
}

async function handleContinue() {
  if (selected.value.length === 0) return;

  if (selectedWithVars.value.length > 0) {
    loadingVars.value = true;
    try {
      const varIds = selectedWithVars.value.map(q => q.id);
      const vars = await rpc('qq-variables', { body: { qq_ids: varIds } });
      qqVariables.value = Array.isArray(vars) ? vars : [];
      // Pre-fill overrides
      const defaults = {};
      for (const v of qqVariables.value) {
        defaults[`${v.quick_quotation_id}:${v.name}`] = v.value;
      }
      overrides.value = defaults;
    } catch (err) {
      console.error('[QQ] Failed to load variables:', err);
    }
    loadingVars.value = false;
    step.value = 'variables';
  } else {
    step.value = 'confirm';
  }
}

function setOverride(qqId, varName, value) {
  overrides.value[`${qqId}:${varName}`] = value;
}

function buildOverrides() {
  if (selectedWithVars.value.length === 0) return undefined;
  const result = {};
  for (const [key, val] of Object.entries(overrides.value)) {
    const varName = key.split(':')[1];
    if (varName && val) {
      const num = parseFloat(val);
      result[varName] = isNaN(num) ? val : num;
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

function getResolvedName(q) {
  if (!hasVariables(q)) return q.display_name || q.name;
  const vars = {};
  for (const [key, val] of Object.entries(overrides.value)) {
    const [qqId, varName] = key.split(':');
    if (qqId === q.id) vars[varName] = val;
  }
  return resolveName(q.name, vars);
}

function getVarsForQQ(qqId) {
  return qqVariables.value.filter(v => v.quick_quotation_id === qqId);
}

async function handleCreate() {
  if (selected.value.length === 0) return;
  sending.value = true;
  try {
    const overrides = buildOverrides();
    for (const qqId of selected.value) {
      await rpc('create-quotation', {
        body: {
          qq_id: qqId,
          phone: props.phone,
          conversation_id: props.conversationId,
          variable_overrides: overrides,
        },
      });
    }
    emit('created');
    emit('close');
  } catch (err) {
    console.error('[QQ] Error creating quotation:', err);
    alert('Error al crear cotización: ' + (err.message || err));
  }
  sending.value = false;
}

function handleBack() {
  if (step.value === 'confirm') {
    step.value = selectedWithVars.value.length > 0 ? 'variables' : 'select';
  } else if (step.value === 'variables') {
    step.value = 'select';
  }
}

function stepTitle() {
  if (step.value === 'select') return 'Cotizacion rapida';
  if (step.value === 'variables') return 'Ajustar medidas';
  return 'Confirmar cotizacion';
}
</script>

<template>
  <ModalWrapper :show="show" :title="stepTitle()" @close="emit('close')">
    <!-- Progress bar -->
    <div class="qq-progress">
      <div
        v-for="i in 3"
        :key="i"
        class="qq-progress-bar"
        :class="{
          'qq-progress-done': i - 1 < stepIndex,
          'qq-progress-active': i - 1 === stepIndex,
        }"
      />
    </div>

    <!-- Step 1: Select -->
    <template v-if="step === 'select'">
      <div class="qq-search-wrap">
        <input
          v-model="search"
          class="qq-search-input"
          type="text"
          placeholder="Buscar producto..."
          autofocus
        />
      </div>

      <div class="qq-list">
        <div
          v-for="q in filtered"
          :key="q.id"
          class="qq-list-item"
          :class="{ 'qq-list-item--selected': selected.includes(q.id) }"
          @click="toggleItem(q.id)"
        >
          <input
            type="checkbox"
            class="qq-checkbox"
            :checked="selected.includes(q.id)"
            @click.stop
            @change="toggleItem(q.id)"
          />
          <div class="qq-list-info">
            <span class="qq-list-name">{{ q.display_name || q.name }}</span>
            <span v-if="q.tags && q.tags.length" class="qq-list-tags">
              {{ q.tags.join(', ') }}
            </span>
            <span v-if="hasVariables(q)" class="qq-var-badge">
              medidas editables
            </span>
          </div>
        </div>
        <div v-if="filtered.length === 0" class="qq-empty">
          Sin resultados
        </div>
      </div>

      <div class="qq-footer">
        <span class="qq-footer-count">
          {{ selected.length }} seleccionado{{ selected.length !== 1 ? 's' : '' }}
        </span>
        <button
          class="qq-btn qq-btn-primary"
          :disabled="selected.length === 0 || loadingVars"
          @click="handleContinue"
        >
          <span v-if="loadingVars" class="qq-spinner" />
          Continuar
        </button>
      </div>
    </template>

    <!-- Step 2: Variables -->
    <template v-if="step === 'variables'">
      <div class="qq-vars-scroll">
        <div
          v-for="q in selectedWithVars"
          :key="q.id"
          class="qq-var-group"
        >
          <div class="qq-var-title">{{ getResolvedName(q) }}</div>
          <div class="qq-var-fields">
            <div
              v-for="v in getVarsForQQ(q.id)"
              :key="v.id"
              class="qq-var-field"
            >
              <label class="qq-var-label">{{ v.name }}</label>
              <input
                type="number"
                step="0.01"
                class="qq-var-input"
                :value="overrides[`${q.id}:${v.name}`] || ''"
                @input="setOverride(q.id, v.name, $event.target.value)"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="qq-footer">
        <button class="qq-btn qq-btn-secondary" @click="handleBack">
          Volver
        </button>
        <button class="qq-btn qq-btn-primary" @click="step = 'confirm'">
          Continuar
        </button>
      </div>
    </template>

    <!-- Step 3: Confirm -->
    <template v-if="step === 'confirm'">
      <div class="qq-confirm-list">
        <div
          v-for="q in selectedItems"
          :key="q.id"
          class="qq-confirm-item"
        >
          <span class="qq-confirm-icon">⚡</span>
          <span class="qq-confirm-name">{{ getResolvedName(q) }}</span>
        </div>
      </div>

      <div class="qq-footer">
        <button class="qq-btn qq-btn-secondary" @click="handleBack">
          Volver
        </button>
        <button
          class="qq-btn qq-btn-primary"
          :disabled="sending"
          @click="handleCreate"
        >
          <span v-if="sending" class="qq-spinner" />
          {{ sending ? 'Creando...' : 'Crear cotizacion' }}
        </button>
      </div>
    </template>
  </ModalWrapper>
</template>

<style scoped>
/* Progress bar */
.qq-progress {
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
}
.qq-progress-bar {
  flex: 1;
  height: 3px;
  border-radius: 2px;
  background: rgb(var(--slate-4, 232 232 236));
  transition: background 0.2s;
}
.qq-progress-bar.qq-progress-done {
  background: rgb(var(--green-9, 48 164 108));
}
.qq-progress-bar.qq-progress-active {
  background: rgb(var(--blue-9, 0 120 244));
}

/* Search */
.qq-search-wrap {
  margin-bottom: 12px;
}
.qq-search-input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid rgb(var(--slate-5, 220 220 224));
  border-radius: 10px;
  font-size: 14px;
  outline: none;
  background: rgb(var(--slate-2, 248 248 249));
  color: rgb(var(--slate-12, 28 32 36));
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.qq-search-input:focus {
  border-color: rgb(var(--blue-9, 0 120 244));
}
.qq-search-input::placeholder {
  color: rgb(var(--slate-9, 140 144 150));
}

/* List */
.qq-list {
  max-height: 40vh;
  overflow-y: auto;
  margin: 0 -20px;
  padding: 0 20px;
}
.qq-list-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s;
}
.qq-list-item:hover {
  background: rgb(var(--slate-3, 240 240 243));
}
.qq-list-item--selected {
  background: rgb(var(--blue-2, 237 244 254));
}
.qq-list-item--selected:hover {
  background: rgb(var(--blue-3, 224 237 253));
}
.qq-checkbox {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  accent-color: rgb(var(--blue-9, 0 120 244));
  cursor: pointer;
}
.qq-list-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.qq-list-name {
  font-size: 13px;
  font-weight: 500;
  color: rgb(var(--slate-12, 28 32 36));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.qq-list-tags {
  font-size: 11px;
  color: rgb(var(--slate-9, 140 144 150));
}
.qq-var-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  color: rgb(var(--blue-9, 0 120 244));
  background: rgb(var(--blue-2, 237 244 254));
  padding: 1px 6px;
  border-radius: 4px;
  width: fit-content;
  margin-top: 2px;
}
.qq-empty {
  text-align: center;
  color: rgb(var(--slate-9, 140 144 150));
  font-size: 13px;
  padding: 24px 0;
}

/* Footer */
.qq-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid rgb(var(--slate-4, 232 232 236));
}
.qq-footer-count {
  font-size: 12px;
  color: rgb(var(--slate-9, 140 144 150));
}

/* Buttons */
.qq-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;
}
.qq-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.qq-btn-primary {
  background: rgb(var(--blue-9, 0 120 244));
  color: #fff;
}
.qq-btn-primary:hover:not(:disabled) {
  background: rgb(var(--blue-10, 0 105 218));
}
.qq-btn-secondary {
  background: rgb(var(--slate-3, 240 240 243));
  color: rgb(var(--slate-11, 96 100 108));
}
.qq-btn-secondary:hover:not(:disabled) {
  background: rgb(var(--slate-4, 232 232 236));
}

/* Variables step */
.qq-vars-scroll {
  max-height: 40vh;
  overflow-y: auto;
}
.qq-var-group {
  margin-bottom: 16px;
}
.qq-var-title {
  font-size: 13px;
  font-weight: 600;
  color: rgb(var(--slate-12, 28 32 36));
  margin-bottom: 8px;
}
.qq-var-fields {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.qq-var-field {
  display: flex;
  align-items: center;
  gap: 10px;
}
.qq-var-label {
  font-size: 12px;
  color: rgb(var(--slate-11, 96 100 108));
  min-width: 60px;
}
.qq-var-input {
  flex: 1;
  padding: 7px 10px;
  border: 1px solid rgb(var(--slate-5, 220 220 224));
  border-radius: 8px;
  font-size: 13px;
  outline: none;
  background: rgb(var(--slate-2, 248 248 249));
  color: rgb(var(--slate-12, 28 32 36));
  max-width: 120px;
}
.qq-var-input:focus {
  border-color: rgb(var(--blue-9, 0 120 244));
}

/* Confirm step */
.qq-confirm-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 40vh;
  overflow-y: auto;
}
.qq-confirm-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: rgb(var(--slate-2, 248 248 249));
  border-radius: 8px;
}
.qq-confirm-icon {
  flex-shrink: 0;
  font-size: 14px;
}
.qq-confirm-name {
  font-size: 13px;
  font-weight: 500;
  color: rgb(var(--slate-12, 28 32 36));
}

/* Spinner */
.qq-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: qqSpin 0.6s linear infinite;
}
.qq-btn-secondary .qq-spinner {
  border-color: rgba(0,0,0,0.15);
  border-top-color: rgb(var(--slate-11, 96 100 108));
}
@keyframes qqSpin {
  to { transform: rotate(360deg); }
}
</style>
